import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import * as api from "./api";

// Sesiunea, o singură dată pentru toată aplicația.
//
// `loading` e distinct de „neautentificat": fără el, orice refresh ar clipi
// ecranul de login înainte ca /api/auth/me să răspundă, iar un deep-link ar
// arunca userul afară dintr-o pagină la care avea acces.
type SessionValue = {
  user: api.CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: api.CurrentUser) => void;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<api.CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  /** CINE E OMUL — cerut o dată, la deschidere, CU O A DOUA ÎNCERCARE.
   *
   *  Un singur răspuns prost însemna «nu ești logat». Dar răspunsul ăla poate
   *  să nu fie despre om: un deploy repornește control plane-ul, iar cererea
   *  care prinde repornirea se întoarce fără nimic sau cu un 401 de la un
   *  worker abia pornit. Cine reîncărca pagina chiar atunci — adică exact ce
   *  face omul după un deploy, ca să vadă ce s-a schimbat — era dus la login
   *  cu sesiunea încă bună în cookie (01.10.2026, măsurat pe VM: un singur 401
   *  pe `/api/auth/me`, cu secretul și contul neatinse).
   *
   *  O SINGURĂ REÎNCERCARE, și scurtă. Cât ține ea, ecranul stă pe «se
   *  încarcă» — starea pe care o avea oricum —, deci un om chiar delogat
   *  așteaptă o jumătate de secundă în plus până vede login-ul. Mai multe
   *  încercări ar face din deconectarea adevărată o așteptare, ceea ce e mai
   *  rău decât defectul reparat.
   *
   *  NU ACOPERĂ UN COOKIE CU ADEVĂRAT RESPINS: dacă serverul îl refuză și a
   *  doua oară, se merge la login ca până acum. Repară clipa, nu starea. */
  useEffect(() => {
    let viu = true;
    const asteapta = (ms: number) => new Promise((gata) => setTimeout(gata, ms));
    void (async () => {
      for (let incercare = 0; ; incercare++) {
        try {
          const om = await api.me({ silent: true });
          if (viu) { setUser(om); setLoading(false); }
          return;
        } catch {
          if (incercare >= 1) break;
          await asteapta(500);
          if (!viu) return;
        }
      }
      if (viu) { setUser(null); setLoading(false); }
    })();
    return () => { viu = false; };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setUser(await api.login(email, password));
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
  }, []);

  return (
    <SessionContext.Provider value={{ user, loading, signIn, signOut, setUser }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession folosit în afara lui SessionProvider");
  return value;
}
